import map from "lodash/map";
import React, { FunctionComponent, useEffect, useMemo, useRef } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Panel } from "../../../../states/customization";
import { Content } from "../../../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../../../types/customization/template";
import { lengthMmToPx } from "../../../../utils/customization/box";
import { evalContentCondition } from "../../../../utils/customization/condition";
import {
  findColorDefinition,
  findPageAndDefinition,
} from "../../../../utils/customization/finders";
import { sortElementsForRendering } from "../../../../utils/customization/page";
import Box from "../../../atoms/customization/Box";
import EditablePicture from "../EditablePicture";
import EditablePicturePreview from "../EditablePicturePreview";
import EditableText from "../EditableText";
import ForcedImage from "../ForcedImage";
import ForcedText from "../ForcedText";
import "./style.scss";

interface Props {
  content: Content;
  forcedColorKey?: string;
  forcedPageDefinition?: TemplatePageDefinition;
  pageKey: string;
  ppmm: number;
  skipEvents?: boolean;
  template: Template;
}

const Page: FunctionComponent<Props> = ({
  content,
  forcedColorKey,
  forcedPageDefinition,
  pageKey,
  ppmm,
  skipEvents = false,
  template,
}) => {
  const {
    dispatch,
    ui: { pageKeyToFocus },
  } = useCustomizationContext();
  const { page, pageDefinition } = findPageAndDefinition(pageKey, template, content);
  const usedPageDefinition = forcedPageDefinition ? forcedPageDefinition : pageDefinition;

  const focusTarget = useRef<HTMLDivElement>(null);
  const isFocusedPage = useMemo(() => pageKeyToFocus === pageKey, [pageKeyToFocus, pageKey]);

  useEffect(() => {
    if (isFocusedPage && focusTarget.current && skipEvents === false) {
      // delayed to avoid wrong position due to animations
      window.setTimeout(() => {
        if (focusTarget.current) {
          focusTarget.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          dispatch({ type: "setPageToFocus", pageKey: null });
        }
      }, 800);
    }
  }, [dispatch, focusTarget, isFocusedPage, pageKey, skipEvents]);

  const colorKeyToUse = forcedColorKey ? forcedColorKey : page.backgroundColorKey;
  const colorDefinition = findColorDefinition(colorKeyToUse, template.colorDefinitions);

  const style = {
    backgroundColor: colorDefinition?.value || "transparent",
    height: lengthMmToPx(usedPageDefinition.height, ppmm).value,
    width: lengthMmToPx(usedPageDefinition.width, ppmm).value,
  };

  const boxes = map(
    sortElementsForRendering(usedPageDefinition.elementDefinitions),
    (elementDefinition) => {
      let element;
      let forced = false;

      const contentConditionValidated =
        elementDefinition.conditions &&
        evalContentCondition(elementDefinition.conditions, template, content);
      if (contentConditionValidated === false) {
        if (elementDefinition.type === "editable-picture") {
          dispatch({
            editablePictureKey: elementDefinition.key,
            pageKey,
            type: "removePicture",
            undoKey: "ignore",
          });
        } else if (elementDefinition.type === "editable-text") {
          dispatch({
            editableTextKey: elementDefinition.key,
            pageKey,
            type: "removeText",
            undoKey: "ignore",
          });
        }
        return null;
      }

      switch (elementDefinition.type) {
        case "editable-picture":
          if (forcedPageDefinition) {
            element = (
              <EditablePicturePreview
                elementDefinitionKey={elementDefinition.key}
                page={page}
                pageDefinition={usedPageDefinition}
                ppmm={ppmm}
                template={template}
              />
            );
          } else {
            element = (
              <EditablePicture
                elementDefinitionKey={elementDefinition.key}
                page={page}
                pageDefinition={usedPageDefinition}
                ppmm={ppmm}
                template={template}
              />
            );
          }
          if (skipEvents) {
            forced = true;
          }
          break;

        case "editable-text":
          element = (
            <EditableText
              content={content}
              elementDefinitionKey={elementDefinition.key}
              page={page}
              pageDefinition={usedPageDefinition}
              ppmm={ppmm}
              template={template}
            />
          );
          if (skipEvents) {
            forced = true;
          }
          break;

        case "forced-image":
          element = (
            <ForcedImage
              content={content}
              elementDefinitionKey={elementDefinition.key}
              pageDefinition={usedPageDefinition}
              ppmm={ppmm}
              template={template}
            />
          );
          forced = true;
          break;

        case "forced-text":
          forced = true;
          let onClickForcedText: (() => void) | undefined;
          if (
            elementDefinition.forcedType === "interpolated-content" &&
            elementDefinition.interpolatedContent === "book_title"
          ) {
            onClickForcedText = () => {
              dispatch({ type: "setPanel", panel: Panel.EDITTITLE });
            };
            forced = false;
          }
          element = (
            <ForcedText
              content={content}
              elementDefinition={elementDefinition}
              onClick={onClickForcedText}
              page={page}
              pageDefinition={usedPageDefinition}
              ppmm={ppmm}
              template={template}
            />
          );
          break;

        default: {
          const exhaustiveCheck: never = elementDefinition;
          throw new Error(`Unsupported type ${(exhaustiveCheck as { type: string }).type}`);
        }
      }

      return (
        <Box
          key={elementDefinition.key}
          elementDimensions={elementDefinition}
          pageDimensions={usedPageDefinition}
          ppmm={ppmm}
          zIndex={elementDefinition.zIndex}
          forced={forced}
        >
          {element}
        </Box>
      );
    }
  );

  return (
    <div key={pageKey} className="Page" style={style} ref={isFocusedPage ? focusTarget : null}>
      {boxes}
    </div>
  );
};

export default Page;
